import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import { useState } from 'react';
import { useAppState } from 'hooks/useAppState';
import type { VoiceChatResponseType } from 'types/api/voiceChatResponseType';

import type { JSX } from 'react';

/**
 * ボイスチャット作成編集 画面埋め込み用タグコンポーネント（モック 1137-1245 行相当）。
 * 公開後（予約含む）にのみ埋め込みタグを生成する。
 */
export default function WsVoiceChatEditScreenEmbed(props: {
  voiceChatState: VoiceChatResponseType | null;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [copied, setCopied] = useState(false);
  const { noticeMessageSuccess } = useAppState();

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 公開情報の表示可否を返す
   */
  const _isVisiblePublishInfo = (): boolean => {
    if (!props.voiceChatState) {
      return false;
    }
    if (props.voiceChatState.voiceChatStatusDiv === DivConst.items.voiceChatStatus.reserved.value) {
      return true;
    }
    return props.voiceChatState.publishedAt != null;
  };

  const _generateEmbedCode = (): string => {
    if (!props.voiceChatState || !_isVisiblePublishInfo()) {
      return '';
    }

    const staticEndpoint = config.s3.bucket.staticContents.endpoint.replace(/\/$/, '');
    const userWebUrl = config.url.userWeb.replace(/\/$/, '');
    const embedJsUrl = `${staticEndpoint}/common/embed/branq-voice-chat-embed.js`;
    const cfgJson = JSON.stringify({ staticEndpoint, userWebUrl });

    return [
      '<!-- BranQ Voice Chat Screen Embed Tag -->',
      '<div id="branq-voice-chat-embed"></div>',
      '<script>',
      '(function branqVoiceChatEmbedBootstrap(w,d,s,u,c,e,t){',
      'c=w.BranqVoiceChatEmbed=w.BranqVoiceChatEmbed||function(){(c.q=c.q||[]).push(arguments)};',
      'c.q=c.q||[];',
      `c.cfg=${cfgJson};`,
      'e=d.createElement(s);e.async=1;e.src=u;',
      't=d.getElementsByTagName(s)[0];t.parentNode.insertBefore(e,t);',
      `})(window,document,'script',${JSON.stringify(embedJsUrl)});`,
      `BranqVoiceChatEmbed(${JSON.stringify(props.voiceChatState.cd)});`,
      '</script>',
    ].join('\n');
  };

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  const onCopyCode = async () => {
    const embedCode = _generateEmbedCode();
    if (!embedCode) return;
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      noticeMessageSuccess('コードをコピーしました。');
    } catch (err) {
      console.error('コピーに失敗しました:', err);
    }
  };

  const embedCode = _generateEmbedCode();
  const hasEmbedCode = embedCode !== '';

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <section className="section-form-default">
      <div className="container">
        <div className="box-common shadow-none">
          <h3 className="title-section title-md">画面埋め込み用タグ</h3>
          <div className="form-default-container">
            <div className="form-group">
              <p className="mb-3">
                下のコードをコピーして、ボイスチャットを表示したい場所に追加してください。ページ内にそのまま埋め込まれます。
              </p>
              <div className="position-relative">
                <textarea
                  className="form-control font-monospace"
                  rows={8}
                  readOnly
                  value={hasEmbedCode ? embedCode : ''}
                  placeholder="公開設定を行うと、画面埋め込み用タグが表示されます。"
                  style={{ paddingRight: '50px' }}
                />
                <button
                  onClick={onCopyCode}
                  disabled={!hasEmbedCode}
                  className="btn-empty position-absolute"
                  style={{ top: '8px', right: '8px', zIndex: 10 }}
                  title={copied ? 'コピーしました' : 'コピー'}
                >
                  {copied ? (
                    <img src="/img/icon/copied.svg" alt="コピー済み" />
                  ) : (
                    <img src="/img/icon/copy.svg" alt="コピー" />
                  )}
                </button>
              </div>
              <p className="mt-2 text-muted small">
                ※コンテンツ公開時のみ表示されます。
                <br />
                ※埋め込み枠のサイズはタグ内の width / height で調整できます。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
